/**
 * Слой платформы: всё, что зависит от Telegram, — только здесь.
 * Потом рядом появятся реализации для MAX и Capacitor с тем же интерфейсом.
 */
export interface Platform {
  name: 'telegram' | 'browser';
  /** Заголовок Authorization для API. */
  authHeader(): string;
  /** Параметр запуска (startapp), например `inv_<token>`. */
  startParam: string | null;
  /** Тёмная ли тема у самого мессенджера. */
  prefersDark(): boolean;
  onThemeChange(cb: () => void): () => void;
  ready(): void;
  haptic(kind: 'light' | 'success' | 'warning' | 'error'): void;
  /** Системная кнопка «Назад» мессенджера; null — скрыть. */
  setBack(handler: (() => void) | null): void;
  /** Поделиться ссылкой (приглашение в семью). false — не получилось, ссылку покажем на экране. */
  shareLink(url: string, text: string): Promise<boolean>;
  confirm(message: string): Promise<boolean>;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string };
  colorScheme: 'light' | 'dark';
  ready(): void;
  expand(): void;
  disableVerticalSwipes?(): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
  safeAreaInset?: Insets;
  contentSafeAreaInset?: Insets;
  openTelegramLink(url: string): void;
  showConfirm(message: string, cb: (ok: boolean) => void): void;
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium'): void;
    notificationOccurred(type: 'success' | 'warning' | 'error'): void;
  };
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
}

interface Insets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/** Отступы от выреза экрана и кнопок Telegram (полноэкранный режим) → CSS-переменные. */
function applyInsets(tg: TelegramWebApp) {
  const root = document.documentElement.style;
  const set = (prefix: string, v?: Insets) => {
    if (!v) return;
    root.setProperty(`--tg-${prefix}-top`, `${v.top}px`);
    root.setProperty(`--tg-${prefix}-bottom`, `${v.bottom}px`);
  };
  set('safe-area-inset', tg.safeAreaInset);
  set('content-safe-area-inset', tg.contentSafeAreaInset);
}

function telegram(): TelegramWebApp | null {
  const tg = (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;
  return tg && tg.initData ? tg : null;
}

function telegramPlatform(tg: TelegramWebApp): Platform {
  let backHandler: (() => void) | null = null;
  const onBack = () => backHandler?.();
  tg.BackButton.onClick(onBack);
  return {
    name: 'telegram',
    authHeader: () => `tma ${tg.initData}`,
    startParam: tg.initDataUnsafe.start_param ?? null,
    prefersDark: () => tg.colorScheme === 'dark',
    onThemeChange(cb) {
      tg.onEvent('themeChanged', cb);
      return () => tg.offEvent('themeChanged', cb);
    },
    ready() {
      tg.ready();
      tg.expand();
      tg.disableVerticalSwipes?.();
      applyInsets(tg);
      for (const ev of ['safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged', 'viewportChanged']) tg.onEvent(ev, () => applyInsets(tg));
    },
    haptic(kind) {
      if (kind === 'light') tg.HapticFeedback?.impactOccurred('light');
      else tg.HapticFeedback?.notificationOccurred(kind);
    },
    setBack(handler) {
      backHandler = handler;
      if (handler) tg.BackButton.show();
      else tg.BackButton.hide();
    },
    async shareLink(url, text) {
      tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`);
      return true;
    },
    confirm: (message) => new Promise((resolve) => tg.showConfirm(message, resolve)),
  };
}

/** Обычный браузер — для разработки: вход заголовком `dev <id>:<имя>` (сервер должен быть с DEV_AUTH=1). */
function browserPlatform(): Platform {
  const params = new URLSearchParams(location.search);
  let devUser = params.get('dev');
  try {
    if (devUser) localStorage.setItem('devUser', devUser);
    else devUser = localStorage.getItem('devUser');
  } catch {
    /* хранилище может быть недоступно */
  }
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  return {
    name: 'browser',
    authHeader: () => {
      // Заголовки HTTP — только латиница, поэтому имя кодируем.
      const [id, name = ''] = (devUser ?? '1001:Мама').split(':');
      return `dev ${id}:${encodeURIComponent(name)}`;
    },
    startParam: params.get('startapp'),
    prefersDark: () => media.matches,
    onThemeChange(cb) {
      media.addEventListener('change', cb);
      return () => media.removeEventListener('change', cb);
    },
    ready() {},
    haptic() {},
    setBack() {},
    async shareLink(url) {
      try {
        await navigator.clipboard.writeText(url);
        return true;
      } catch {
        return false;
      }
    },
    confirm: (message) => Promise.resolve(window.confirm(message)),
  };
}

const tg = telegram();
export const platform: Platform = tg ? telegramPlatform(tg) : browserPlatform();
